import PostHog from 'analytics/posthog';
import CheckoutAnalytics from 'analytics/checkout';
import { setRollbarPerson } from 'analytics/rollbar';
import { shippingTier } from 'ecommerce/lib/checkoutShipping';

import FacebookPixel from './scripts/facebookPixel';
import Gtag from './scripts/gtag';
import OmnisendScript from './scripts/omnisend';
import LogDNA from './scripts/logdna';

import SETTINGS from '../settings';

let timeout;

// GA4 parameters per custom event. PostHog payloads can carry coupon codes and ids
// GA4 must not get, so only these keys are forwarded.
const GA4_PARAMS = {
  coupon_claim_rejected: ['coupon_result'],
  signin_email_error: ['error_type', 'context'],
  signup_email_error: ['error_type', 'context'],
};

const pickParams = (payload, keys) =>
  Object.fromEntries(keys.filter(key => payload[key] != null).map(key => [key, payload[key]]));

const tracker = {
  /**
   * /pages/_app.js
   */
  onAppInit: () => {
    // Unconmment to log data
    // console.log('TRACKER', 'onAppInit');

    LogDNA.init();
    PostHog.init(SETTINGS.SCRIPTS.POSTHOG_KEY, SETTINGS.SCRIPTS.POSTHOG_HOST);
    // The CheckoutAnalytics failure listener is registered at module load (end of file), not here.
  },

  /**
   * /pages/_app.js
   */
  pageView: (route, extraProps = {}) => {
    setTimeout(() => {
      // Unconmment to log data
      // console.log('TRACKER', 'pageView', route, extraProps);

      FacebookPixel.pageView();
      Gtag.pageView();
      OmnisendScript.pageView();
      PostHog.pageView(extraProps);
    }, 200);
  },

  productView: skuProduct => {
    if (!skuProduct) {
      return;
    }

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      FacebookPixel.productView(skuProduct.id, skuProduct.name, skuProduct.price, skuProduct.currency);
      Gtag.productView(skuProduct.id, skuProduct.name, skuProduct.price, skuProduct.currency);
    }, 200);
  },

  /**
   * /pages/_app.js
   */
  cartItemAdd: skuProduct => {
    // Unconmment to log data
    // console.log('TRACKER', 'cartItemAdd', skuProduct);

    FacebookPixel.cartItemAdd(skuProduct.id, skuProduct.name, skuProduct.price, skuProduct.currency);
    Gtag.cartItemAdd(skuProduct.id, skuProduct.name, skuProduct.price, skuProduct.currency);
  },

  /**
   * /pages/cart/index.js
   */
  checkoutInit: (price, currency, promoCode, items, overall = price) => {
    // Unconmment to log data
    // console.log('TRACKER', 'checkoutInit');

    FacebookPixel.checkoutInit(price, currency, items);
    Gtag.checkoutInit(price, currency, promoCode, items);
    CheckoutAnalytics.started(overall, currency);
  },

  /**
   * /pages/cart/index.js - Apple Pay / Google Pay pay on the cart page and never reach
   * /cart/checkout, so begin_checkout is sent on the wallet click (PBP-325).
   * CheckoutPaymentRequest already sends the PostHog checkout_init itself.
   */
  walletCheckoutInit: (wallet, price, currency, promoCode, items) => {
    Gtag.expressPayClick(wallet);
    FacebookPixel.checkoutInit(price, currency, items);
    Gtag.checkoutInit(price, currency, promoCode, items);
  },

  async orderPayment(type, operation) {
    const form = CheckoutAnalytics.currentPaymentForm();
    let result;
    try {
      result = await operation();
    } catch (error) {
      tracker.orderPaymentFailure(type, form, 'client_error');
      throw error;
    }

    if (result.error && !result.orderCreated) {
      tracker.orderPaymentFailure(type, form, result.errorCode);
      return result;
    }

    const order = result.data;
    const paymentInfo = order.meta_data?.payment_info;
    CheckoutAnalytics.succeeded(type, paymentInfo?.overall, paymentInfo?.currency, order.id, form);
    Gtag.addPaymentInfo(
      paymentInfo?.overall,
      paymentInfo?.currency,
      CheckoutAnalytics.paymentMethod(type),
      paymentInfo?.items ?? [],
    );
    // Legacy GA4 name, kept until reports move to add_payment_info (PBP-273).
    Gtag.customEvent('checkout_payment');
    return result;
  },

  /**
   * /pages/cart/checkout.js, /pages/cart/amazon.js - before any validation, so
   * clicks without a purchase stay visible
   */
  checkoutPayClick: (paymentType, price, currency) => {
    Gtag.checkoutPayClick(CheckoutAnalytics.paymentMethod(paymentType), price, currency);
  },

  /**
   * /pages/cart/checkout.js - one event per invalid field on each submit
   */
  checkoutFieldErrors: errors => {
    errors.forEach(error => Gtag.checkoutFieldError(error.field, error.type));
  },

  /**
   * /pages/cart/index.js - wallet and Amazon Pay buttons
   */
  expressPayClick: wallet => {
    Gtag.expressPayClick(wallet);
  },

  /**
   * /pages/cart/checkout.js, /pages/cart/amazon.js
   */
  giftNoteToggle: selected => {
    Gtag.giftNoteToggle(selected);
  },

  giftNoteWritten: messageLength => {
    Gtag.giftNoteWritten(messageLength);
  },

  /**
   * /pages/cart/index.js - once per cart page visit
   */
  cartView: (price, currency, items) => {
    Gtag.viewCart(price, currency, items);
  },

  /**
   * /pages/cart/index.js, /pages/cart/checkout.js, /pages/cart/amazon.js -
   * shipping is confirmed when the order is submitted
   */
  shippingConfirm: (price, currency, shippingType, items) => {
    Gtag.addShippingInfo(price, currency, shippingTier(shippingType), items);
  },

  /**
   * /pages/_app.js - after the user confirms the removal
   */
  cartItemRemove: (item, context) => {
    Gtag.removeFromCart(item, context);
  },

  /**
   * /pages/cart/checkout.js - tags checkout outcomes with the rendered form
   * while the page is mounted, pass null on unmount.
   */
  paymentFormSet: form => {
    CheckoutAnalytics.setPaymentForm(form);
  },

  /**
   * /pages/cart/checkout.js - experiment exposure, once per page mount.
   */
  paymentFormExposed: form => {
    CheckoutAnalytics.paymentFormExposed(form);
  },

  // GA4 gets the failure through the CheckoutAnalytics listener (paymentFailure).
  orderPaymentFailure: (type, form, errorCode) => {
    CheckoutAnalytics.failed(type, form, errorCode);
  },

  paymentFailure: (paymentMethod, errorCode) => {
    Gtag.paymentFailure(paymentMethod, errorCode ?? 'unknown');
  },

  /**
   * /pages/cart/_confirmation.js
   */
  orderSend: orderData => {
    // Unconmment to log data
    // console.log('TRACKER', 'orderSend', orderData);

    const { external_id: externalOrderId } = orderData;
    const { payment_info: paymentInfo } = orderData.meta_data;
    const {
      items,
      overall: price,
      currency,
      applied_promo_code: promoCode,
      shipping,
    } = paymentInfo;

    FacebookPixel.orderSend(price, currency, externalOrderId, items);
    Gtag.orderSend(price, currency, externalOrderId, promoCode, shipping, items);
  },

  /**
   * /pages/_app.js
   */
  userSet: user => {
    // Unconmment to log data
    // console.log('TRACKER', 'userSet', user);

    if (typeof window !== 'undefined' && window.Rollbar) {
      setRollbarPerson(window.Rollbar, user.id, user.email);
    }
    PostHog.identify(user.id, user.email, user.name);
  },

  /**
   * /pages/_app.js - called on sign out to reset analytics identity
   */
  userClear: () => {
    PostHog.reset();
  },

  /**
   * /pages/cart/index.js
   * /pages/cart/amazon.js
   * /pages/cart/checkout.js
   */
  cartSet: (items, price, currency, email = '') => {
    // Unconmment to log data
    // console.log('TRACKER', 'cartSet', { items, price, currency, email });
  },

  /**
   * /pages/product/[productId].js
   */
  editorInit: sku => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorInit', sku);
  },

  /**
   * /pages/_app.js
   */
  editorChoosePhotos: maximumNumberOfPhotos => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorChoosePhotos', maximumNumberOfPhotos);
  },

  /**
   * /pages/_app.js
   */
  editorUploadPhotos: numberOfPhotos => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorUploadPhotos', numberOfPhotos);
  },

  /**
   * /pages/_app.js
   */
  editorPreviewPrints: () => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorPreviewPrints');
  },

  /**
   * /pages/editor/[...params].js
   */
  editorPreviewDone: (skuProduct, data) => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorPreviewDone', { skuProduct, data });
  },

  /**
   * /pages/_app.js
   */
  editorUpgradeProduct: skuProduct => {
    // Unconmment to log data
    // console.log('TRACKER', 'editorUpgradeProduct', skuProduct);
  },

  /**
   * /pages/signin.js
   */
  signIn: userData => {
    // Unconmment to log data
    // console.log('TRACKER', 'signIn', userData);

    Gtag.customEvent('signin_email_success');
    PostHog.customEvent('signin_email_success');
  },

  /**
   * /pages/signin.js
   */
  signUp: userData => {
    // Unconmment to log data
    // console.log('TRACKER', 'signUp', userData);

    Gtag.customEvent('signup_email_success');
    PostHog.customEvent('signup_email_success');
  },

  /**
   * /pages/signin.js
   */
  signInWithAmazon: userData => {
    // Unconmment to log data
    // console.log('TRACKER', 'signInWithAmazon', userData);

    Gtag.customEvent('signin_amazon_success');
    PostHog.customEvent('signin_amazon_success');
  },

  /**
   * /pages/signin.js
   */
  signInWithFacebook: userData => {
    // Unconmment to log data
    // console.log('TRACKER', 'signInWithFacebook', userData);

    Gtag.customEvent('signin_facebook_success');
    PostHog.customEvent('signin_facebook_success');
  },

  /**
   * /pages/_app.js
   */
  onCustomEvent: (action, payload = {}) => {
    // Unconmment to log data
    // console.log('TRACKER', 'onCustomEvent', action, payload);

    if (action === 'editor_thumbnail_summary') {
      PostHog.customEvent(action, {
        ...payload,
        app_version: SETTINGS.VERSION,
        release: SETTINGS.SCRIPTS.COMMIT_HASH,
        environment: SETTINGS.SCRIPTS.ROLLBAR_ENV,
      }, ['hidden', 'pagehide', 'exit'].includes(payload.flush_reason)
        ? { transport: 'sendBeacon', send_instantly: true } : undefined);
      return;
    }

    if (GA4_PARAMS[action]) {
      Gtag.event(action, pickParams(payload, GA4_PARAMS[action]));
    } else {
      Gtag.customEvent(action);
    }
    PostHog.customEvent(action, payload);
  },

  /**
   * /pages/_app.js - mirrors the persisted cart promo code as a PostHog super
   * property so every event in the session can be broken down by coupon.
   */
  promoCodeSet: code => {
    // Unconmment to log data
    // console.log('TRACKER', 'promoCodeSet', code);

    if (code) {
      PostHog.register({ coupon_code: code });
    } else {
      PostHog.unregister('coupon_code');
    }
  },
};

// Failures caught in shared checkout components (card, Affirm, wallet) reach GA4 too.
// Registered here at module load, not in onAppInit. Scripts calls onAppInit in a
// 2 s setTimeout after mount, so a failure in that window reached PostHog but never
// GA4 (parabo-next-o5dg). Registering early is safe: Gtag's track retries when gtag
// has not loaded yet, like every other event.
CheckoutAnalytics.setFailureListener(tracker.paymentFailure);

export default tracker;
